import { FlixMediaUI } from "./FlixMediaUI";
import { StandardProps } from "../componentMap";
import React, { useEffect, useRef, useState } from "react";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { attachScript, cleanUp } from "./utils";
import debounce from "lodash/debounce";
import { dispatchAnalyticsEvent } from "../../utils";
import { noticeError } from "../../utils/newrelic/requests";

export function FlixMedia({
  product,
  runtimeConfig,
}: Pick<StandardProps, "product" | "runtimeConfig">) {
  const { variant } = useVariant(product);
  const flixMediaContainerRef = useRef<HTMLDivElement>(null);
  const flixMediaInpageRef = useRef<HTMLDivElement>(null);
  const [isFlixAvailable, setIsFlixAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const { skuId } = variant.aliases;
  const brand = product.brand?.name || "";
  const { price, title } = variant;
  const value = price?.value || "0.00";
  const {
    browsePageHierarchy,
    aliases: { pimProductId },
  } = product;
  const category = browsePageHierarchy[1]
    ? browsePageHierarchy[1].name
    : // @ts-ignore
      browsePageHierarchy[0].name;

  useEffect(() => () => cleanUp(), []);

  useEffect(() => {
    setIsLoading(true);
    setIsFlixAvailable(false);

    const url = `${runtimeConfig.flixMediaBaseUrl}/${skuId}`;

    fetch(url)
      .then((res) => {
        res.json().then((json) => {
          const matchFound = json["event"] === "matchhit";
          setIsFlixAvailable(matchFound);
        });
      })
      .catch((e: Error) => {
        noticeError("Could not successfully retrieve flixMedia data", {
          message: e.message,
          stack: e.stack || "",
        });
      });
  }, [skuId]);

  useEffect(() => {
    if (!isFlixAvailable) {
      return;
    }

    cleanUp();
    attachScript(flixMediaContainerRef.current!, skuId, brand.toLowerCase());

    const setContentLoaded = debounce((observer: MutationObserver) => {
      setIsLoading(false);
      observer.disconnect();
    }, 1000);

    const observer = new MutationObserver((_, observer) =>
      setContentLoaded(observer),
    );

    observer.observe(flixMediaInpageRef.current!, {
      childList: true,
      attributes: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [isFlixAvailable]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    flixMediaContainerRef.current!.scrollIntoView();
    if (!isOpen) {
      dispatchAnalyticsEvent(
        "global.interaction",
        {
          type: "pdp",
          subType: "take a closer look - show more",
          additionalInfo: {},
          properties: {},
          products: [
            {
              brand: brand,
              category: category,
              sku: skuId,
              price: value,
              productName: title,
              productId: pimProductId,
            },
          ],
        },
        {},
      );
    }
  };

  if (!isFlixAvailable) return null;

  return (
    <FlixMediaUI
      isLoading={isLoading}
      isOpen={isOpen}
      handleToggle={handleToggle}
      flixMediaContainerRef={flixMediaContainerRef}
      flixMediaInpageRef={flixMediaInpageRef}
      props={
        {
          name: "FlixMedia",
        } as React.ComponentPropsWithoutRef<"div">
      }
    />
  );
}
